import { useState, useEffect } from 'react';
import { db } from '../../../firebase';
import { collection, getDocs, addDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { useTheme } from '../../../context/ThemeContext';
import { FiPlus, FiTrash2, FiMail } from 'react-icons/fi';

const BlogEditorsManager = () => {
  const [editors, setEditors] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    fetchEditors();
  }, []);

  const fetchEditors = async () => {
    try {
      const q = query(collection(db, 'blog_editors'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const editorsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setEditors(editorsData);
    } catch (error) {
      console.error('Erro ao buscar editores:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEditor = async (e) => {
    e.preventDefault();
    if (!newEmail.trim() || !newEmail.includes('@')) return;

    // Check if already exists
    const exists = editors.some(ed => ed.email.toLowerCase() === newEmail.trim().toLowerCase());
    if (exists) {
      alert('Este e-mail já está cadastrado como editor.');
      return;
    }

    setSaving(true);
    try {
      const docRef = await addDoc(collection(db, 'blog_editors'), {
        email: newEmail.trim().toLowerCase(),
        createdAt: serverTimestamp()
      });
      
      setEditors([{
        id: docRef.id,
        email: newEmail.trim().toLowerCase(),
        createdAt: new Date()
      }, ...editors]);
      setNewEmail('');
    } catch (error) {
      console.error('Erro ao adicionar editor:', error);
      alert('Erro ao adicionar editor.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja remover este editor?')) return;
    try {
      await deleteDoc(doc(db, 'blog_editors', id));
      setEditors(editors.filter(ed => ed.id !== id));
    } catch (error) {
      console.error('Erro ao remover editor:', error);
      alert('Erro ao remover editor.');
    }
  };

  if (loading) return <div className="p-8 text-center">Carregando editores...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Equipe de Editores (Blog)
          </h2>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Adicione e-mails de pessoas que poderão acessar o painel apenas para gerenciar o Blog.
          </p>
        </div>
      </div>

      {/* Add form */}
      <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <form onSubmit={handleAddEditor} className="flex gap-4 items-end">
          <div className="flex-1">
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              E-mail do Editor (ex: usuario@gmail.com)
            </label>
            <div className="relative">
              <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Digite o e-mail"
                className={`w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                required
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={saving || !newEmail}
            className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md font-medium flex items-center gap-2 disabled:opacity-50 transition-colors h-10"
          >
            <FiPlus />
            Adicionar
          </button>
        </form>
      </div>

      {/* List */}
      <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <table className="w-full text-left">
          <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
            <tr>
              <th className={`px-6 py-3 text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>E-mail</th>
              <th className={`px-6 py-3 text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Adicionado em</th>
              <th className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-right ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {editors.length === 0 ? (
              <tr>
                <td colSpan="3" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                  Nenhum editor cadastrado.
                </td>
              </tr>
            ) : (
              editors.map(editor => (
                <tr key={editor.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className={`px-6 py-4 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {editor.email}
                  </td>
                  <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {editor.createdAt?.seconds ? new Date(editor.createdAt.seconds * 1000).toLocaleDateString() : 'Recente'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleDelete(editor.id)}
                      className="text-red-500 hover:text-red-700 p-2"
                      title="Remover editor"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BlogEditorsManager;
